import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { ZipBuildProgress } from '../lib/clientZip'

type Props = {
  title: string
  progress: ZipBuildProgress
  onCancel: () => void
}

function progressSummary(progress: ZipBuildProgress) {
  if (progress.phase === 'assembling') return 'Assembling archive'
  return `Downloading files ${progress.completed} of ${progress.total}`
}

function progressDetail(progress: ZipBuildProgress) {
  if (progress.phase === 'assembling') return 'The browser is packaging the downloaded images into a ZIP file.'
  if (!progress.currentFile) return 'Preparing download…'
  return progress.currentFile
}

export default function ZipProgressDialog({ title, progress, onCancel }: Props) {
  const bodyScrollYRef = useRef(0)

  useEffect(() => {
    const { body } = document
    const previousStyles = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width
    }

    bodyScrollYRef.current = window.scrollY
    body.style.overflow = 'hidden'
    body.style.position = 'fixed'
    body.style.top = `-${bodyScrollYRef.current}px`
    body.style.width = '100%'

    return () => {
      body.style.overflow = previousStyles.overflow
      body.style.position = previousStyles.position
      body.style.top = previousStyles.top
      body.style.width = previousStyles.width
      window.scrollTo(0, bodyScrollYRef.current)
    }
  }, [])

  return createPortal(
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="zip-progress-title">
      <div className="modal-card" onClick={(event) => event.stopPropagation()}>
        <h3 id="zip-progress-title" style={{ marginTop: 0, marginBottom: 6 }}>Preparing ZIP</h3>
        <div><small className="muted">{title}</small></div>
        <div className="zip-progress-block">
          <div className="zip-progress-summary">{progressSummary(progress)}</div>
          <progress className="zip-progress-bar" value={progress.completed} max={Math.max(progress.total, 1)} />
          <div className="zip-progress-meta">
            <small className="muted">{progressDetail(progress)}</small>
          </div>
        </div>
        <div className="row" style={{ marginTop: 12 }}>
          <button type="button" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>,
    document.body
  )
}
